import React, { useState, useEffect, useMemo } from 'react';
import { Plus, ChefHat, IndianRupee, Trash2, Pencil, Upload, AlertCircle, Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { getPreps, deletePrep, producePrep, getIngredientsMaster } from '../lib/api';
import { exportPrepsCsv, exportPrepsXlsx } from '../lib/exportTemplate';
import { useOutlet } from '../lib/OutletContext';
import { useAuth } from '../lib/AuthContext';
import logger from '../lib/logger';
import { formatCurrency, formatNumber } from '../lib/utils';
import PrepEditorDialog from '../components/PrepEditorDialog';
import PrepUploadDialog from '../components/PrepUploadDialog';

const Prep = () => {
  const { selectedOutlet } = useOutlet();
  const { user } = useAuth();
  const [preps, setPreps] = useState([]);
  const [prepsCount, setPrepsCount] = useState(0);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Search — usage-aware (matches prep_name + ingredient names inside the
  // prep's recipe). Debounced 250ms before hitting the API.
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Editor (create or edit)
  const [editorMode, setEditorMode] = useState('create');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPrep, setEditorPrep] = useState(null);

  // Upload modal
  const [uploadOpen, setUploadOpen] = useState(false);

  // Produce dialog
  const [produceOpen, setProduceOpen] = useState(false);
  const [producePrepObj, setProducePrepObj] = useState(null);
  const [actualYield, setActualYield] = useState('');
  const [producing, setProducing] = useState(false);

  const canEdit = !!(user?.is_org_admin || user?.is_super_admin);

  // Recipe-list refetches whenever search changes; ingredients are side data
  // (master pricing for cost rollup) and refetch only on outlet change.
  useEffect(() => {
    loadPreps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutlet, debouncedSearch]);

  useEffect(() => {
    loadIngredients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutlet]);

  const loadPreps = async () => {
    if (!selectedOutlet) return;
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      const prepsResp = await getPreps(params);
      // Backwards-compatible unwrap: /api/preps currently returns a bare array,
      // but a future envelope shape ({preps: [...]}) is also handled.
      const prepsList = Array.isArray(prepsResp) ? prepsResp : (prepsResp?.preps || []);
      // Outlet scope: include org-wide preps (outlet_id null/undefined) AND
      // preps explicitly bound to the currently selected outlet. Never hide
      // org-wide preps just because an outlet is selected.
      const visiblePreps = prepsList.filter((p) =>
        !p.outlet_id || p.outlet_id === selectedOutlet
      );
      setPreps(visiblePreps);
      setPrepsCount(visiblePreps.length);
    } catch (e) {
      toast.error('Failed to load preps');
      logger.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadIngredients = async () => {
    if (!selectedOutlet) return;
    try {
      const ingResp = await getIngredientsMaster(selectedOutlet, true);
      const ingList = Array.isArray(ingResp) ? ingResp : (ingResp?.ingredients || []);
      setIngredients(ingList);
    } catch (e) {
      logger.warn('[Prep] ingredients fetch failed', e);
    }
  };

  // loadAll preserved for callers (handleDelete, handleProduce) that expect
  // a single 'reload everything' entry-point. Equivalent to the two splits.
  const loadAll = async () => {
    await Promise.all([loadPreps(), loadIngredients()]);
  };

  // Alphabetical preps by name
  const sortedPreps = useMemo(() => {
    return [...preps].sort((a, b) =>
      ((a.prep_name || '')).localeCompare((b.prep_name || ''), undefined, { sensitivity: 'base' })
    );
  }, [preps]);

  const handleDelete = async (prep) => {
    if (!window.confirm(`Delete prep "${prep.prep_name}"?\nIf it has ledger history, it will be archived instead.`)) return;
    try {
      const result = await deletePrep(prep.prep_id);
      if (result.action === 'archived') toast.info(`Archived: ${result.reason}`);
      else toast.success(`Deleted: ${prep.prep_name}`);
      loadAll();
    } catch (e) {
      toast.error('Failed to delete prep');
      logger.error(e);
    }
  };

  const handleProduce = async () => {
    if (!producePrepObj || !actualYield) return;
    setProducing(true);
    try {
      await producePrep(producePrepObj.prep_id, selectedOutlet, parseFloat(actualYield));
      toast.success('Prep produced successfully');
      setProduceOpen(false); setActualYield(''); setProducePrepObj(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to produce prep');
      logger.error(e);
    } finally {
      setProducing(false);
    }
  };

  const openCreate = () => { setEditorMode('create'); setEditorPrep(null); setEditorOpen(true); };
  const openEdit = (p) => { setEditorMode('edit'); setEditorPrep(p); setEditorOpen(true); };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="prep-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="prep-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <ChefHat className="w-7 h-7 text-emerald-500" />
            Prep / Mise-en-Place
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Manage prep production and recipes</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={!preps?.length}
                className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors text-sm"
                data-testid="export-preps-button"
                title="Export the currently visible preps in the bulk-import format"
              >
                <Download className="w-4 h-4" /> Export Preps
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#18181b] border-[#27272a]">
              <DropdownMenuItem
                onClick={() => {
                  const ingMap = new Map((ingredients || []).map((i) => [i.ingredient_id, i]));
                  const n = exportPrepsCsv(preps, ingMap);
                  toast.success(`Exported ${preps.length} prep${preps.length === 1 ? '' : 's'} (${n} line${n === 1 ? '' : 's'}) to CSV.`);
                }}
                data-testid="export-preps-csv"
              >CSV</DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  const ingMap = new Map((ingredients || []).map((i) => [i.ingredient_id, i]));
                  const n = exportPrepsXlsx(preps, ingMap);
                  toast.success(`Exported ${preps.length} prep${preps.length === 1 ? '' : 's'} (${n} line${n === 1 ? '' : 's'}) to Excel.`);
                }}
                data-testid="export-preps-xlsx"
              >Excel (.xlsx)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={() => setUploadOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md transition-colors text-sm"
            data-testid="bulk-import-button"
            title="Upload CSV / XLSX"
          >
            <Upload className="w-4 h-4" /> Bulk Import
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md transition-colors font-bold text-sm"
            data-testid="create-prep-button"
          >
            <Plus className="w-4 h-4" /> Create Prep
          </button>
        </div>
      </div>

      {/* Search bar — usage-aware (matches by prep_name OR by ingredient
          names / ids inside the prep's recipe). Backend escapes user input
          so meta-chars like '(' '*' '++' never crash the API. */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search by prep name, ingredient, or unit…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-20 py-2 bg-[#18181b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
            data-testid="prep-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs text-zinc-400 hover:text-zinc-200 hover:bg-[#27272a] rounded"
              data-testid="prep-search-clear-button"
              title="Clear search"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <div className="text-xs text-zinc-500" data-testid="prep-result-count">
          {debouncedSearch
            ? <>Showing <span className="text-zinc-200 font-medium">{prepsCount}</span> for &ldquo;{debouncedSearch}&rdquo;</>
            : <>{prepsCount} prep{prepsCount === 1 ? '' : 's'}</>}
          {searchTerm && searchTerm !== debouncedSearch && (
            <span className="ml-2 italic">searching…</span>
          )}
        </div>
      </div>

      {/* List */}
      {sortedPreps.length === 0 ? (
        <div className="text-center py-16 bg-[#18181b] border border-[#27272a] rounded-lg" data-testid="empty-state">
          <ChefHat className="w-16 h-16 mx-auto mb-3 text-zinc-600" />
          {debouncedSearch ? (
            <>
              <p className="text-zinc-400 mb-4" data-testid="prep-empty-search">
                No preps match &ldquo;{debouncedSearch}&rdquo;.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="text-emerald-500 hover:text-emerald-400 font-medium"
                data-testid="prep-empty-clear-search"
              >
                Clear search
              </button>
            </>
          ) : (
            <>
              <p className="text-zinc-400 mb-4">No prep definitions found</p>
              <button onClick={openCreate} className="text-emerald-500 hover:text-emerald-400 font-medium">+ Create your first prep</button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prep-grid">
          {sortedPreps.map((prep) => {
            const stockAvg = prep.costing?.stock_avg_cost ?? 0;
            const stockCostUnavailable = !!prep.costing?.stock_cost_unavailable;
            const stockValue = prep.costing?.stock_value ?? ((prep.current_stock || 0) * stockAvg);
            return (
              <div key={prep.prep_id} className="bg-[#18181b] border border-[#27272a] rounded-lg p-4 hover:border-emerald-500/40 transition-colors" data-testid="prep-card">
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <ChefHat className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <h3 className="font-bold truncate" data-testid="prep-name">{prep.prep_name}</h3>
                      <p className="text-xs text-zinc-500 mt-0.5">Expected: {formatNumber(prep.expected_yield)} {prep.base_unit}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canEdit && (
                      <button
                        onClick={() => openEdit(prep)}
                        className="p-1.5 text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 rounded transition-colors"
                        title="Edit prep"
                        data-testid="edit-prep-btn"
                      ><Pencil className="w-4 h-4" /></button>
                    )}
                    <button
                      onClick={() => handleDelete(prep)}
                      className="p-1.5 text-red-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      title="Delete or archive"
                      data-testid="delete-prep-btn"
                    ><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                  <div className="bg-[#09090b] p-2 rounded">
                    <p className="text-zinc-500">Current Stock</p>
                    <p className="font-mono font-bold" data-testid="prep-stock">{formatNumber(prep.current_stock)} {prep.base_unit}</p>
                  </div>
                  <div className="bg-[#09090b] p-2 rounded">
                    <p className="text-zinc-500">Stock Value</p>
                    {stockCostUnavailable ? (
                      <p
                        className="font-mono font-bold text-yellow-400 text-[11px] flex items-center gap-1"
                        title="Stock exists but average cost is unknown. Inputs to this prep are missing avg_cost. Produce a fresh batch with priced inputs to seed the cost."
                        data-testid="prep-stock-value-unavailable"
                      >
                        <AlertCircle className="w-3 h-3" /> Cost unavailable
                      </p>
                    ) : (
                      <p className="font-mono font-bold" data-testid="prep-stock-value">{formatCurrency(stockValue)}</p>
                    )}
                  </div>
                </div>

                <div className="bg-emerald-500/5 border border-emerald-500/20 p-2 rounded mb-3" data-testid="prep-cost-summary">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-zinc-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" />Prep Costing</p>
                    {prep.costing?.missing_costs > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {prep.costing.missing_costs} missing
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-zinc-500">Batch Cost</p>
                      <p className="font-mono font-bold" data-testid="prep-batch-cost">{formatCurrency(prep.costing?.total_cost ?? 0)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Per {prep.costing?.yield_unit || prep.base_unit}</p>
                      <p className="font-mono font-bold text-emerald-400" data-testid="prep-unit-cost">{formatCurrency(prep.costing?.cost_per_unit ?? 0)}</p>
                    </div>
                  </div>
                </div>

                <div className="text-xs space-y-1 mb-3 max-h-32 overflow-y-auto pr-1">
                  <p className="text-zinc-500 font-medium uppercase tracking-wider mb-1">Recipe</p>
                  {(prep.costing?.costed_lines || prep.recipe || []).map((line, idx) => {
                    const displayName = line.ingredient_name && line.ingredient_name.trim()
                      ? line.ingredient_name
                      : (line.ingredient_id ? `Unknown (${String(line.ingredient_id).slice(0, 8)}…)` : 'Unknown');
                    return (
                      <div key={idx} className="flex items-center justify-between gap-2 text-zinc-300">
                        <span className="truncate flex-1 min-w-0" data-testid={`prep-line-name-${idx}`}>{displayName}</span>
                        <span className="text-zinc-500 font-mono shrink-0">{formatNumber(line.qty)} {line.base_unit}</span>
                        <span className={`font-mono shrink-0 w-16 text-right ${line.line_cost ? '' : 'text-zinc-600'}`} data-testid={`prep-line-cost-${idx}`}>{line.line_cost ? formatCurrency(line.line_cost) : '—'}</span>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={() => { setProducePrepObj(prep); setActualYield(String(prep.expected_yield || '')); setProduceOpen(true); }}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-md transition-colors text-sm"
                  data-testid="produce-prep-button"
                >Produce Prep</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor (create + edit) */}
      <PrepEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        mode={editorMode}
        existingPrep={editorPrep}
        ingredients={ingredients}
        onSaved={() => loadAll()}
      />

      {/* Upload */}
      <PrepUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        ingredients={ingredients}
        existingPreps={preps}
        onImported={() => loadAll()}
      />

      {/* Produce */}
      <Dialog open={produceOpen} onOpenChange={(o) => { if (!o) { setProduceOpen(false); setProducePrepObj(null); } }}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white" data-testid="produce-dialog">
          <DialogHeader>
            <DialogTitle>Produce {producePrepObj?.prep_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Expected Yield</label>
              <input type="number" value={producePrepObj?.expected_yield || 0} disabled className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm opacity-50" />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Actual Yield *</label>
              <input
                type="number" step="0.01" value={actualYield}
                onChange={(e) => setActualYield(e.target.value)} autoFocus
                data-testid="actual-yield-input"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
            </div>
            <button
              type="button" onClick={handleProduce} disabled={producing}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50"
              data-testid="confirm-produce-button"
            >{producing ? 'Producing…' : 'Confirm Production'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Prep;
